import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Shield, LogIn } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_helpdesk-hub-53/artifacts/dtuif3s8_NEWGamerLogo.jpg";

// MSAL Configuration - uses environment variables
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin + '/login',
    postLogoutRedirectUri: window.location.origin + '/login',
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

// Only initialize MSAL if Azure is configured
let msalInstance = null;
if (msalConfig.auth.clientId) {
  msalInstance = new PublicClientApplication(msalConfig);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, ssoLogin, isAuthenticated, loading: authLoading } = useAuth();
  
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [processingRedirect, setProcessingRedirect] = useState(false);

  // Initialize MSAL and handle redirect response
  useEffect(() => {
    const initMsal = async () => {
      if (!msalInstance) {
        setMsalInitialized(false);
        return;
      }

      try {
        await msalInstance.initialize();
        setMsalInitialized(true);
        
        // Handle redirect response if coming back from Microsoft login
        setProcessingRedirect(true);
        const response = await msalInstance.handleRedirectPromise();
        
        if (response && response.idToken) {
          console.log('Got token from redirect:', response.account?.username);
          try {
            await ssoLogin(response.idToken);
            // Redirect to saved URL or dashboard
            const returnUrl = sessionStorage.getItem('returnUrl');
            sessionStorage.removeItem('returnUrl');
            navigate(returnUrl || '/');
            return;
          } catch (err) {
            console.error('SSO backend error:', err);
            setError(`SSO failed: ${err.response?.data?.detail || err.message}`);
            setShowAdminForm(true);
          }
        }
      } catch (err) {
        console.error('MSAL init/redirect error:', err);
        if (err.errorCode && err.errorCode !== 'no_account_error') {
          setError(`Microsoft login error: ${err.message}`);
        }
      } finally {
        setProcessingRedirect(false);
      }
    };
    
    initMsal();
  }, [ssoLogin, navigate]);

  // Get the return URL from session storage
  const getReturnUrl = () => {
    const returnUrl = sessionStorage.getItem('returnUrl');
    sessionStorage.removeItem('returnUrl');
    return returnUrl || '/';
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !processingRedirect) {
      navigate(getReturnUrl());
    }
  }, [isAuthenticated, navigate, processingRedirect]);

  const handleSSOLogin = async () => {
    if (!msalInstance || !msalInitialized) {
      setError('Microsoft SSO is not configured. Use admin login.');
      setShowAdminForm(true);
      return;
    }

    setSsoLoading(true);
    setError('');

    try {
      // Use redirect instead of popup - more reliable
      await msalInstance.loginRedirect(loginRequest);
      // Page will redirect to Microsoft, then back here
    } catch (err) {
      console.error('SSO redirect error:', err);
      setSsoLoading(false);
      setError(`SSO login failed: ${err.message}. Try admin login instead.`);
      setShowAdminForm(true);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username, password);
      navigate(getReturnUrl());
    } catch (err) {
      console.error('Admin login error:', err);
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || processingRedirect) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="text-sm text-slate-500">
          {processingRedirect ? 'Completing sign in...' : 'Loading...'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-4">
          <img 
            src={LOGO_URL} 
            alt="Gamer Packaging" 
            className="h-16 mx-auto object-contain"
          />
          <div>
            <CardTitle className="text-2xl font-bold text-slate-800">IT Help Desk</CardTitle>
            <CardDescription className="text-slate-500">
              Sign in to access the dashboard
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertDescription className="text-red-700">{error}</AlertDescription>
            </Alert>
          )}

          {/* Microsoft SSO Button */}
          <Button
            onClick={handleSSOLogin}
            disabled={ssoLoading || !msalInitialized}
            className="w-full h-12 bg-[#0078d4] hover:bg-[#106ebe] text-white font-medium"
            data-testid="sso-login-btn"
          >
            {ssoLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <svg className="h-5 w-5 mr-2" viewBox="0 0 21 21" fill="currentColor">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
            )}
            {ssoLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
          </Button>

          {!msalInitialized && msalConfig.auth.clientId && (
            <p className="text-xs text-center text-amber-600">
              Initializing Microsoft login...
            </p>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">Or</span>
            </div>
          </div>

          {/* Admin Login Toggle */}
          {!showAdminForm ? (
            <Button
              variant="outline"
              onClick={() => setShowAdminForm(true)}
              className="w-full h-10 text-slate-600 hover:text-slate-800"
              data-testid="show-admin-form-btn"
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin Login
            </Button>
          ) : (
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Username</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  required
                  autoComplete="username"
                  data-testid="admin-username-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-slate-800 hover:bg-slate-900"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <LogIn className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAdminForm(false)}
                className="w-full text-sm text-slate-500"
              >
                Cancel
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
